/**
 * CatalogPicker — modal-style picker that lets the user select a SKU from
 * product_catalog grouped by category. Used by invoice / estimate builders.
 *
 * Props
 *   onSelect(item)   — called with the selected catalog row when user picks
 *   onCancel()       — close without selecting
 *   neuShadow        — boxShadow for the active item highlight
 *
 * Behavior
 *   - Calls catalog:list + catalog:get-categories on mount
 *   - Sticky search bar filters across category + name
 *   - Click a row → calls onSelect with the full row
 *   - Esc / click-outside closes via onCancel
 *
 * Design ref: docs/product_catalog_design_2026-06-06.md
 */
"use strict";

import React, { useState, useEffect, useMemo, useRef } from "react";
import { Search, X } from "lucide-react";

function fmtCents(cents) {
  if (!Number.isFinite(cents)) return "—";
  return "$" + (cents / 100).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

export default function CatalogPicker({ onSelect, onCancel, neuShadow }) {
  const [items, setItems] = useState([]);
  const [categories, setCategories] = useState([]);
  const [search, setSearch] = useState("");
  const [activeCategory, setActiveCategory] = useState("all");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const overlayRef = useRef(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        // public/apiClient.js exposes window.zignagenixAPI.invoke(channel, ...args)
        // for both Electron and web (the web version proxies to POST /api/ipc).
        const api = window.zignagenixAPI;
        if (!api || typeof api.invoke !== "function") {
          throw new Error("API not ready");
        }
        const [list, cats] = await Promise.all([
          api.invoke("catalog:list"),
          api.invoke("catalog:get-categories"),
        ]);
        if (cancelled) return;
        setItems(Array.isArray(list) ? list : []);
        setCategories(Array.isArray(cats) ? cats : []);
        setLoading(false);
      } catch (e) {
        if (cancelled) return;
        setError(e && e.message || "Failed to load catalog");
        setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    function onKey(e) { if (e.key === "Escape") onCancel && onCancel(); }
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [onCancel]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return items.filter(i => {
      if (activeCategory !== "all" && i.category !== activeCategory) return false;
      if (!q) return true;
      return (
        (i.display_name || "").toLowerCase().includes(q) ||
        (i.sku || "").toLowerCase().includes(q) ||
        (i.category || "").toLowerCase().includes(q)
      );
    });
  }, [items, search, activeCategory]);

  // Group filtered items by category for display.
  const grouped = useMemo(() => {
    const m = new Map();
    for (const i of filtered) {
      if (!m.has(i.category)) m.set(i.category, []);
      m.get(i.category).push(i);
    }
    return Array.from(m.entries());
  }, [filtered]);

  function handleOverlayClick(e) {
    if (e.target === overlayRef.current) onCancel && onCancel();
  }

  function categoryLabel(cat) {
    const found = categories.find(c => c.category === cat);
    return found ? found.label : cat;
  }

  return (
    <div
      ref={overlayRef}
      onClick={handleOverlayClick}
      style={{
        position: "fixed", inset: 0, background: "rgba(15, 23, 42, 0.55)",
        display: "flex", alignItems: "center", justifyContent: "center",
        zIndex: 9000, padding: 20,
      }}
    >
      <div
        style={{
          background: "#F8F9FA", borderRadius: 14, padding: 0,
          width: "100%", maxWidth: 760, maxHeight: "85vh",
          display: "flex", flexDirection: "column", overflow: "hidden",
          boxShadow: "0 18px 48px rgba(0,0,0,0.22)",
        }}
      >
        {/* Header */}
        <div style={{
          padding: "14px 18px", borderBottom: "1px solid #E9ECEF",
          display: "flex", alignItems: "center", justifyContent: "space-between",
        }}>
          <div>
            <h3 style={{ margin: 0, fontSize: 16, fontWeight: 700, color: "#212529" }}>Select a product</h3>
            <p style={{ margin: "2px 0 0", fontSize: 11, color: "#6C757D" }}>From the master catalog · prices are baseline; overrides happen on the next step</p>
          </div>
          <button
            type="button"
            onClick={onCancel}
            aria-label="Close"
            style={{ background: "transparent", border: "none", cursor: "pointer", padding: 6, borderRadius: 8 }}
          >
            <X size={18} color="#6C757D" />
          </button>
        </div>

        {/* Filter bar */}
        <div style={{ padding: "10px 18px", display: "flex", gap: 8, alignItems: "center", borderBottom: "1px solid #E9ECEF" }}>
          <div style={{
            flex: 1, display: "flex", alignItems: "center", gap: 6,
            background: "#fff", borderRadius: 10, padding: "6px 10px",
            border: "1px solid #DEE2E6",
          }}>
            <Search size={14} color="#6C757D" />
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search by name, SKU, or category"
              autoFocus
              style={{ border: "none", outline: "none", flex: 1, fontSize: 13, background: "transparent" }}
            />
          </div>
          <select
            value={activeCategory}
            onChange={(e) => setActiveCategory(e.target.value)}
            style={{
              background: "#fff", border: "1px solid #DEE2E6", borderRadius: 10,
              padding: "6px 8px", fontSize: 13, color: "#212529", cursor: "pointer",
            }}
          >
            <option value="all">All categories</option>
            {categories.map(c => (
              <option key={c.category} value={c.category}>{c.label}</option>
            ))}
          </select>
        </div>

        {/* List */}
        <div style={{ flex: 1, overflowY: "auto", padding: "8px 12px" }}>
          {loading && <p style={{ color: "#6C757D", fontSize: 13, padding: 12 }}>Loading catalog…</p>}
          {error && <p style={{ color: "#B91C1C", fontSize: 13, padding: 12 }}>Error: {error}</p>}
          {!loading && !error && grouped.length === 0 && (
            <p style={{ color: "#6C757D", fontSize: 13, padding: 12 }}>No matching products.</p>
          )}
          {grouped.map(([cat, rows]) => (
            <div key={cat} style={{ marginBottom: 8 }}>
              <h4 style={{
                margin: "8px 6px 6px", fontSize: 11, fontWeight: 700,
                color: "#6C757D", textTransform: "uppercase", letterSpacing: 0.4,
              }}>
                {categoryLabel(cat)} · {rows.length}
              </h4>
              <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
                {rows.map(row => (
                  <button
                    key={row.sku}
                    type="button"
                    onClick={() => onSelect && onSelect(row)}
                    style={{
                      display: "flex", justifyContent: "space-between", alignItems: "center",
                      padding: "10px 14px", borderRadius: 10, border: "none",
                      background: "#fff", cursor: "pointer", textAlign: "left",
                      fontSize: 13, color: "#212529",
                      transition: "background 0.12s ease",
                    }}
                    onMouseEnter={(e) => { e.currentTarget.style.background = "rgba(46,139,152,0.08)"; }}
                    onMouseLeave={(e) => { e.currentTarget.style.background = "#fff"; }}
                  >
                    <div style={{ display: "flex", flexDirection: "column", gap: 2, minWidth: 0 }}>
                      <span style={{ fontWeight: 600, color: "#212529" }}>{row.display_name}</span>
                      <span style={{ fontSize: 11, color: "#6C757D" }}>
                        <span style={{ fontFamily: "ui-monospace, monospace" }}>{row.sku}</span>
                        {row.exosomes_included > 0 ? ` · ${row.exosomes_included} exosomes` : ""}
                        {row.hotel_nights > 0 ? ` · ${row.hotel_nights} hotel night${row.hotel_nights > 1 ? "s" : ""}` : ""}
                      </span>
                    </div>
                    <div style={{ fontSize: 14, fontWeight: 700, color: "#2E8B98", whiteSpace: "nowrap", marginLeft: 12 }}>
                      {fmtCents(row.list_price_cents)}
                    </div>
                  </button>
                ))}
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
